import Page from '../components/Page';
import { useAppStore } from '../stores/useAppStore';
import Map from '../components/Map';
import Card from '../components/Card';
import { useMemo } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import Connector from '../components/Connector';
import { pxToRem } from '../utils/utils';
import AnalyticsWrapper from '../components/AnalyticsWrapper';
import { CAROUSEL_TYPES } from '../constants/carouselTypes';

const Button = ({ children, className = '', onClick, ...props }) => (
  <div
    onClick={onClick}
    {...props}
    className={`
      ${className}
      pt-[1.875rem]
      pb-[1.875rem]
      px-[2.625rem]
      border-[0.3125rem]
      rounded-full
      focus:outline-none
      font-light text-[1.375rem] leading-none
      border-[#3d4e9b] bg-navy
      }
    `}
  >
    {children}
  </div>
);

const MotionButton = motion.create(Button);
const MotionCard = motion.create(Card);

function Page1() {
  const { setCardTouchPos } = useAppStore();
  const { content, mapOptions, setMapOptions, sidebarCollapsed } = useAppStore();
  const { openCarousel } = useAppStore((state) => state.carousel);
  const cards = useMemo(() => content?.demos || [], [content?.demos]);

  const colLeftX = sidebarCollapsed ? "-729" : "-729";
  const colRightX = sidebarCollapsed ? "1822" : "1622";

  const cardPositions = {
    1: [colLeftX, "402", "left", "transH"],
    2: [colLeftX, "598", "left", "transH"],
    3: [colRightX, "8", "right", "transH"], 
    4: [colRightX, "446", "right", "transH"],
    5: [colRightX, "696", "right", "transH"],
    6: [colRightX, "1168", "right", "transH"]
  };  

  const pathsProperties = useMemo(() => {
    const rawProperties = {
      1: { pWidth: -344, pHeight: 0, leftPos: -158, topPos: 580, startDirection: "h", endDirection: "h" },
      2: { pWidth: -726, pHeight: 184, leftPos: -172, topPos: 598, startDirection: "h", endDirection: "h", midPercentage: 13 },
      3: { pWidth: 442, pHeight: 0, leftPos: 1368, topPos: 188, startDirection: "h", endDirection: "h" },
      4: { pWidth: 442, pHeight: 0, leftPos: 1368, topPos: 626, startDirection: "h", endDirection: "h" },
      5: { pWidth: 442, pHeight: 0, leftPos: 1368, topPos: 908, startDirection: "h", endDirection: "h" },
      6: { pWidth: 442, pHeight: 0, leftPos: 1368, topPos: 1348, startDirection: "h", endDirection: "h" },
    };

    const adjustmentWLeft = sidebarCollapsed ? 0 : 220;
    const adjustmentXLeft = 0;

    const adjustmentWRight = 0;
    const adjustmentXRight = 0;
    
    return Object.fromEntries(
      Object.entries(rawProperties).map(([key, value]) => {
        const adjustmentW = value.pWidth < 0 ? adjustmentWRight : adjustmentWLeft;
        const adjustmentX = value.pWidth < 0 ? adjustmentXRight : adjustmentXLeft;
        const adjustedWidth = value.pWidth - (value.pWidth > 0 ? adjustmentW : -adjustmentW);
        const leftPos = pxToRem(value.leftPos - adjustmentX);
        const topPos = pxToRem(value.topPos);

        return [
          key,
          {
            ...value,
            pWidth: adjustedWidth * .5,
            pHeight: value.pHeight * .5,
            leftPos,
            topPos
          },
        ];
      })
    );
  }, [sidebarCollapsed]);
  

  const handleCardClick = (event, cardId) => {
    let x, y;

    if (event.touches) {
      // Evento táctil
      x = event.touches[0].clientX;
      y = event.touches[0].clientY;
    } else {
      // Evento de ratón
      x = event.clientX;
      y = event.clientY;
    }
    setCardTouchPos({x, y});
    AnalyticsWrapper.trackPageView(`/demos/card/${cardId}`);
    
    // Encontrar el índice real en el array original
    const realIndex = cards.findIndex(card => card.id === cardId);
    openCarousel(realIndex, CAROUSEL_TYPES.DEMO);
  };

  const { id } = mapOptions;

  const filteredCards = useMemo(() => {
    return cards.filter(
      (card) =>
        card.id === id
    );
  }, [cards, id]);


  return (
    <Page
      paddingLeft={'55rem'}
      paddingLeftCollapsed={'61rem'}
      className={`
      relative
      flex
      items-center
      `}>
      <div className='relative'>
        <Map />
        <AnimatePresence>
        {(false && mapOptions.pillar !== null) &&
          <MotionButton
            onClick={()=>{setMapOptions({id:null})}}
            animate={{y:0, opacity:1}}
            initial={{y:"+20%", opacity:0}}
            exit={{y:"+20%", opacity:0}}
            className='
            click-effect
            absolute
            left-[4rem]
            top-[97.375rem]
            '
          >BACK TO FULL MAP</MotionButton>
        }
        </AnimatePresence>

        <AnimatePresence>
        { filteredCards.map((card, index) => {
          const connectorProperties = pathsProperties[card.id];
          return (
            <motion.div
              key={`connector_wrapper_${card.id}`}
              className='absolute'
              style={{ left: connectorProperties.leftPos, top: connectorProperties.topPos }}
                
              layout
              transition={{ duration: 0.3, ease: "linear", delay: 0 }}
            >
              <Connector
                id={card.id}
                key={`connector_${card.id}`}
                delay={ 0.2 * index }
                pWidth={connectorProperties.pWidth}
                pHeight={connectorProperties.pHeight}
                startDirection={connectorProperties.startDirection}
                endDirection={connectorProperties.endDirection}
                midPercentage={connectorProperties.midPercentage}
              />
            </motion.div>
          );
        }
        )}
        </AnimatePresence>
        
        <AnimatePresence>
        { filteredCards.map((card, index) => {
          const position = cardPositions[card.id];
          
          return (
            <motion.div
              key={`card_wrapper_${card.id}`}
              className='absolute'
              style={{ left: position[0]*.5, top: position[1]*.5 }}
              
              layout
              transition={{ duration: 0.3, ease: "linear", delay: 0 }}
              >
              
            <MotionCard
              key={`card_${card.id}`}
              item={card}
              color={card.color}
              initial={{ opacity: 0,
                x: position[3] === "transH" ? (position[2] === "left" ? "1rem" : "-1rem") : "0",
                y: position[3] === "transV" ? "-1rem" : "0"
              }}
              animate={{ opacity: 1, x: "0", y: "0" }}
              exit={{ opacity: 0,
                x: position[3] === "transH" ? (position[2] === "left" ? "1rem" : "-1rem") : "0",
                y: position[3] === "transV" ? "-1rem" : "0",
                transition: {duration: 0.3, delay: 0}
              }}
              transition={{ duration: 0.3, delay: (0.2 * index) + 0.7}}
              side={ position[2] }
              onClick={(e) => handleCardClick(e, card.id)}
              onTouchStart={(e) => handleCardClick(e, card.id)}
            />

          </motion.div>
          );
        }
        )}
        </AnimatePresence>
      </div>
          
    </Page>
  );
}

export default Page1;